import React from 'react';

import { v4 as uuidv4 } from 'uuid';

import { Margin, Table, TableBody } from '@zapier/design-system';

import { INVITE_STATUSES, ROLES } from 'app/developer-v3/constants';
import {
  CurrentVersionReadOnlyReason,
  useAvroEvent,
  useCurrentVersionReadOnlyReason,
  useDeleteAdmin,
  useDeleteCollaborator,
  useDeleteSubscriber,
  useQueryAdmins,
  useQueryCollaborators,
  useQuerySubscribers,
  useUpdateAdminContactType,
} from 'app/developer-v3/hooks';

import Member from './Member';
import { sortMembers } from '../utils';

import type { Member as MemberType } from 'app/developer-v3/types/member';

type ContactType = MemberType['contactTypes'][0];

type Props = {
  params: { appId: string };
};

function Members({ params }: Props) {
  const { appId: integrationId } = params;

  const { emitPartnerUserInteractionEvent } = useAvroEvent();

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const { admins } = useQueryAdmins(integrationId);
  const { collaborators } = useQueryCollaborators(integrationId);
  const { subscribers } = useQuerySubscribers(integrationId);
  const members = sortMembers([...admins, ...collaborators, ...subscribers]);

  const { mutate: deleteAdmin } = useDeleteAdmin();
  const { mutate: deleteCollaborator } = useDeleteCollaborator();
  const { mutate: deleteSubscriber } = useDeleteSubscriber();

  const { mutate: updateAdminContactType } = useUpdateAdminContactType();

  const hasMultipleAcceptedAdmins =
    members.filter(
      member =>
        member.status === INVITE_STATUSES.accepted &&
        member.role === ROLES.admin
    ).length > 1;

  const onDeleteMember = ({ id, email, role }: MemberType) => {
    const eventId = uuidv4();
    const causationId = eventId; // causationId is equal to the eventId since onDelete member is the cause
    const correlationId = uuidv4();

    const args = {
      causationId,
      correlationId,
      email,
      integrationId,
      memberId: `${id}`,
    };
    if (role === ROLES.admin) {
      deleteAdmin(args);
    } else if (role === ROLES.collaborator) {
      deleteCollaborator(args);
    } else if (role === ROLES.subscriber) {
      deleteSubscriber(args);
    }

    const hasAccepted = `${id}`.startsWith('a');
    emitPartnerUserInteractionEvent({
      causationId,
      correlationId,
      eventAction: 'click',
      eventId,
      interactionGoal: 'Remove team member from integration',
      interactionName: 'remove_team_member',
      interactionRawText: 'Remove team member',
      targetAccessLevel: role,
      targetCustomuserId: hasAccepted ? Number(`${id}`.slice(1)) : null,
      targetEmailAddress: email,
    });
  };

  const onSetAdminContactType = async (
    member: MemberType,
    contactType: ContactType
  ) => {
    const { email, role } = member;

    if (role !== ROLES.admin) {
      throw `Only admins can be a contact person! role: ${role}`;
    }

    await updateAdminContactType({
      email,
      integrationId,
      typeToAdd: contactType,
    });
  };

  return (
    <Margin margin="20px 0px 0px 0px">
      <Table>
        <TableBody>
          {members.map(member => (
            <Member
              hasMultipleAcceptedAdmins={hasMultipleAcceptedAdmins}
              isNotIntegrationAdmin={isNotAdmin}
              key={`${member.email}-${member.role}-${integrationId}`}
              member={member}
              onDelete={onDeleteMember}
              onSetAdminContactType={contactType =>
                onSetAdminContactType(member, contactType)
              }
            />
          ))}
        </TableBody>
      </Table>
    </Margin>
  );
}

export default Members;
